/* Availability help only. Inventory status and copy remain server-rendered. */
(() => {
  if (customElements.get('jdm-availability-help')) return;

  let instanceId = 0;
  let activeHelp = null;

  class JdmAvailabilityHelp extends HTMLElement {
    connectedCallback() {
      // Also supports Inventory innerHTML replacement and deferred section rendering.
      queueMicrotask(() => {
        if (!this.isConnected || this.listeners) return;
        this.trigger = this.querySelector('.jdm-availability-help__trigger');
        this.panel = this.querySelector('.jdm-availability-help__panel');
        if (!this.trigger || !this.panel) return;

        const id = `JdmAvailabilityHelp-${++instanceId}`;
        this.panel.id = id;
        this.trigger.setAttribute('aria-controls', id);
        this.panel.querySelector('.jdm-availability-help__title').id = `${id}-title`;
        this.panel.querySelector('.jdm-availability-help__text').id = `${id}-text`;
        this.panel.setAttribute('aria-labelledby', `${id}-title`);
        this.panel.setAttribute('aria-describedby', `${id}-text`);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.panel.hidden = true;

        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.trigger.addEventListener('click', () => this.opened ? this.close() : this.open(), options);
        this.panel.querySelector('.jdm-availability-help__close').addEventListener('click', () => this.close(true), options);
      });
    }

    disconnectedCallback() {
      // Removing a variant's Inventory content must also remove any portalled help.
      this.close();
      this.listeners?.abort();
      this.listeners = null;
    }

    open() {
      if (this.opened || !this.isConnected) return;
      activeHelp?.close();
      activeHelp = this;
      this.opened = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      // Escape ancestor overflow/transform clipping without touching theme layout.
      document.body.appendChild(this.panel);
      this.panel.hidden = false;
      this.position();
      this.panel.focus({ preventScroll: true });

      this.openListeners = new AbortController();
      const options = { signal: this.openListeners.signal };
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target) && !this.panel.contains(event.target)) this.close();
      }, options);
      document.addEventListener('focusin', (event) => {
        if (!this.contains(event.target) && !this.panel.contains(event.target)) this.close();
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
        } else if (event.key === 'Tab' && this.panel.contains(event.target)) {
          // Keep the normal page tab order when leaving this non-modal popover.
          const closeButton = this.panel.querySelector('.jdm-availability-help__close');
          if (event.shiftKey || event.target === closeButton) {
            this.close(true);
            if (event.shiftKey) event.preventDefault();
          }
        }
      }, options);
      window.addEventListener('resize', () => this.position(), options);
      window.addEventListener('scroll', () => this.position(), { ...options, capture: true, passive: true });
      window.visualViewport?.addEventListener('resize', () => this.position(), options);
      window.visualViewport?.addEventListener('scroll', () => this.position(), options);
    }

    position() {
      if (!this.opened) return;
      const viewport = window.visualViewport;
      const viewportWidth = viewport?.width || document.documentElement.clientWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const offsetLeft = viewport?.offsetLeft || 0;
      const offsetTop = viewport?.offsetTop || 0;
      const margin = 12;
      const gap = 8;
      const availableWidth = Math.max(0, viewportWidth - margin * 2);
      const availableHeight = Math.max(0, viewportHeight - margin * 2);
      this.panel.style.maxWidth = `${availableWidth}px`;
      this.panel.style.maxHeight = `${availableHeight}px`;
      const anchor = this.trigger.getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      const left = Math.max(offsetLeft + margin,
        Math.min(anchor.right - panel.width, offsetLeft + viewportWidth - panel.width - margin));
      const below = anchor.bottom + gap;
      const above = anchor.top - panel.height - gap;
      const preferredTop = below + panel.height <= offsetTop + viewportHeight - margin ? below : above;
      const top = Math.max(offsetTop + margin,
        Math.min(preferredTop, offsetTop + viewportHeight - panel.height - margin));
      this.panel.style.left = `${Math.round(left)}px`;
      this.panel.style.top = `${Math.round(top)}px`;
    }

    close(restoreFocus = false) {
      if (!this.panel) return;
      this.opened = false;
      this.openListeners?.abort();
      this.openListeners = null;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (this.panel.parentNode !== this) this.appendChild(this.panel);
      if (activeHelp === this) activeHelp = null;
      if (restoreFocus && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define('jdm-availability-help', JdmAvailabilityHelp);
})();
